import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import "./AuthPage.css";
import Logo from "../assets/logo.png";

const AuthPage = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [carNumber, setCarNumber] = useState("");
  const [carModel, setCarModel] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [shouldRedirect, setShouldRedirect] = useState(false);

  const { signUp, signIn, signInWithGoogle, resetPassword, user } = useAuth();
  const navigate = useNavigate();
  // Handle navigation after successful authentication
  useEffect(() => {
    if (user && shouldRedirect) {
      console.log("User authenticated, redirecting to dashboard...");
      console.log("Current location:", window.location.pathname);
      console.log("User object:", user);

      // Try navigate first
      try {
        navigate("/dashboard", { replace: true });
        console.log("Navigate called successfully");

        // Fallback with window.location after a short delay
        setTimeout(() => {
          if (window.location.pathname !== "/dashboard") {
            console.log("Navigate didn't work, using window.location");
            window.location.href = "/dashboard";
          }
        }, 100);
      } catch (error) {
        console.error("Navigation error:", error);
        window.location.href = "/dashboard";
      }

      setShouldRedirect(false);
    }
  }, [user, shouldRedirect, navigate]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (isSignUp && password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (isSignUp && !displayName.trim()) {
      setError("Full Name is required");
      return;
    }
    if (
      isSignUp &&
      (!carNumber.trim() || !carModel.trim() || !mobileNumber.trim())
    ) {
      setError("Car Number, Car Model, and Mobile Number are required");
      return;
    }

    if (
      isSignUp &&
      mobileNumber.trim() &&
      !/^[6-9]\d{9}$/.test(mobileNumber.trim())
    ) {
      setError("Please enter a valid 10-digit mobile number starting with 6-9");
      return;
    }

    setLoading(true);

    try {
      if (isSignUp) {
        await signUp(
          email,
          password,
          displayName,
          carNumber.trim(),
          carModel.trim(),
          mobileNumber.trim()
        );
        setMessage("Account created successfully!");
        setTimeout(() => navigate("/dashboard"), 2000);
      } else {
        console.log("Starting email/password sign-in...");
        await signIn(email, password);
        console.log(
          "Email/password sign-in successful, setting redirect flag..."
        );
        setShouldRedirect(true);
      }
    } catch (error) {
      setError(error.message);
    }

    setLoading(false);
  };
  const handleGoogleSignIn = async () => {
    setError("");
    setLoading(true);

    try {
      console.log("Starting Google sign-in...");
      await signInWithGoogle();
      console.log("Google sign-in successful, setting redirect flag...");
      setShouldRedirect(true);
    } catch (error) {
      console.error("Google sign-in error:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    setLoading(true);

    try {
      await resetPassword(email);
      setMessage("Password reset email sent! Check your inbox.");
      setShowForgotPassword(false);
    } catch (error) {
      setError(error.message);
    }

    setLoading(false);
  };

  if (showForgotPassword) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <Link to="/" className="logo-link">
              <img src={Logo} alt="Steam and Drive" className="auth-logo" />
            </Link>
            <h2>Reset Password</h2>
            <p>
              Enter your email address and we'll send you a link to reset your
              password.
            </p>
          </div>

          <form onSubmit={handleForgotPassword} className="auth-form">
            {error && <div className="error-message">{error}</div>}
            {message && <div className="success-message">{message}</div>}

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
              />
            </div>

            <button type="submit" disabled={loading} className="submit-btn">
              {loading ? "Sending..." : "Send Reset Email"}
            </button>

            <button
              type="button"
              onClick={() => setShowForgotPassword(false)}
              className="link-btn"
            >
              Back to Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <Link to="/" className="logo-link">
            <img src={Logo} alt="Steam and Drive" className="auth-logo" />
          </Link>
          <h2>{isSignUp ? "Create Account" : "Welcome Back"}</h2>
          <p>
            {isSignUp
              ? "Sign up for Steam and Drive"
              : "Sign in to your account"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && <div className="error-message">{error}</div>}
          {message && <div className="success-message">{message}</div>}{" "}
          {isSignUp && (
            <>
              <div className="form-group">
                <label htmlFor="displayName">Full Name</label>
                <input
                  type="text"
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  placeholder="Enter your full name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="carNumber">Car Number</label>
                <input
                  type="text"
                  id="carNumber"
                  value={carNumber}
                  onChange={(e) => setCarNumber(e.target.value.toUpperCase())}
                  required
                  placeholder="e.g., TS09EA1234"
                  style={{ textTransform: "uppercase" }}
                />
              </div>{" "}
              <div className="form-group">
                <label htmlFor="carModel">Car Model</label>
                <input
                  type="text"
                  id="carModel"
                  value={carModel}
                  onChange={(e) => setCarModel(e.target.value)}
                  required
                  placeholder="e.g., Honda City, Maruti Swift"
                />
              </div>
              <div className="form-group">
                <label htmlFor="mobileNumber">Mobile Number</label>{" "}
                <input
                  type="tel"
                  id="mobileNumber"
                  value={mobileNumber}
                  onChange={(e) => setMobileNumber(e.target.value)}
                  required
                  placeholder="e.g., 9876543210"
                  pattern="[6-9][0-9]{9}"
                  maxLength="10"
                />
              </div>
            </>
          )}
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
              minLength="6"
            />
          </div>
          {isSignUp && (
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm your password"
                minLength="6"
              />
            </div>
          )}
          <button type="submit" disabled={loading} className="submit-btn">
            {loading
              ? "Processing..."
              : isSignUp
              ? "Create Account"
              : "Sign In"}
          </button>
          {!isSignUp && (
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="forgot-password-btn"
            >
              Forgot Password?
            </button>
          )}
        </form>

        <div className="divider">
          <span>or</span>
        </div>

        <button
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="google-btn"
        >
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continue with Google
        </button>

        <div className="auth-switch">
          {isSignUp ? (
            <p>
              Already have an account?{" "}
              <button onClick={() => setIsSignUp(false)} className="link-btn">
                Sign In
              </button>
            </p>
          ) : (
            <p>
              Don't have an account?{" "}
              <button onClick={() => setIsSignUp(true)} className="link-btn">
                Sign Up
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
